/**
 * MessageTemplateSelector - A reusable component for selecting and customizing message templates
 * Used when admin clicks Email/SMS/WhatsApp buttons to communicate with customers
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { 
  Mail, MessageSquare, MessageCircle, Send, FileText, Edit2, 
  Loader2, Check, ChevronRight, Sparkles 
} from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';

// Template variable replacements
const replaceTemplateVariables = (text, data) => {
  if (!text) return '';
  
  const replacements = {
    '{{customer_name}}': data.customerName || 'Valued Customer',
    '{{customer_email}}': data.customerEmail || '',
    '{{customer_phone}}': data.customerPhone || '',
    '{{tour_title}}': data.tourTitle || 'Your Tour',
    '{{tour_date}}': data.tourDate || '',
    '{{tour_time}}': data.tourTime || '',
    '{{guests}}': String(data.guests || 1),
    '{{total_price}}': data.totalPrice ? `$${Number(data.totalPrice).toFixed(2)}` : '',
    '{{booking_ref}}': data.bookingRef || data.referenceNumber || '',
    '{{reference_number}}': data.referenceNumber || data.bookingRef || '',
    '{{payment_status}}': data.paymentStatus || 'Pending',
    '{{pickup_location}}': data.pickupLocation || 'Tour meeting point',
    '{{pickup_time}}': data.pickupTime || data.tourTime || '',
    '{{waiver_link}}': data.waiverLink || '',
    '{{company_name}}': 'Tour Finder Puerto Rico',
    '{{company_phone}}': '+1 (787) 929-7723',
  };
  
  let result = text;
  for (const [key, value] of Object.entries(replacements)) {
    result = result.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
  }
  return result;
};

// Default templates for quick setup
const DEFAULT_TEMPLATES = [
  {
    id: 'quick-hello',
    name: 'Quick Hello',
    type: 'all',
    subject: 'Hello from Tour Finder',
    message: 'Hi {{customer_name}}! This is Tour Finder Puerto Rico. How can we help you today?'
  },
  {
    id: 'booking-confirmation',
    name: 'Booking Confirmation',
    type: 'all',
    subject: 'Booking Confirmed - {{tour_title}}',
    message: `Hi {{customer_name}}!

Thank you for booking {{tour_title}} with Tour Finder Puerto Rico!

BOOKING DETAILS:
Tour: {{tour_title}}
Date: {{tour_date}}
Time: {{tour_time}}
Guests: {{guests}}
Total: {{total_price}}
Ref: #{{booking_ref}}

Please arrive 15 minutes early at the meeting point.

Questions? Just reply here!

Best regards,
Tour Finder Puerto Rico`
  },
  {
    id: 'payment-reminder',
    name: 'Payment Reminder',
    type: 'all',
    subject: 'Payment Reminder - {{tour_title}}',
    message: `Hi {{customer_name}}!

This is a friendly reminder about your upcoming tour booking:

Tour: {{tour_title}}
Date: {{tour_date}}
Amount Due: {{total_price}}
Ref: #{{booking_ref}}

Please complete your payment to secure your spot.

Thank you!
Tour Finder Puerto Rico`
  },
  {
    id: 'waiver-reminder',
    name: 'Waiver Reminder',
    type: 'all',
    subject: 'Please Sign Your Waiver - {{tour_title}}',
    message: `Hi {{customer_name}}!

Your tour is coming up and we noticed the waiver hasn't been signed yet.

Tour: {{tour_title}}
Date: {{tour_date}}

Please sign the waiver before your tour date:
{{waiver_link}}

All guests must sign before the tour.

Thank you!
Tour Finder Puerto Rico`
  },
  {
    id: 'tour-reminder',
    name: 'Tour Reminder (Day Before)',
    type: 'all',
    subject: 'Reminder: Your Tour is Tomorrow!',
    message: `Hi {{customer_name}}!

Just a friendly reminder that your tour is TOMORROW!

Tour: {{tour_title}}
Date: {{tour_date}}
Time: {{tour_time}}
Meeting Point: {{pickup_location}}

Please arrive 15 minutes early and bring:
- Comfortable walking shoes
- Sunscreen & hat
- Water bottle
- Camera!

See you tomorrow!
Tour Finder Puerto Rico`
  },
  {
    id: 'thank-you',
    name: 'Thank You (Post Tour)',
    type: 'all',
    subject: 'Thank You for Touring with Us!',
    message: `Hi {{customer_name}}!

Thank you for joining us on {{tour_title}}! We hope you had an amazing experience.

We'd love to hear your feedback! Please leave us a review:
[Review Link]

Book your next adventure with us and get 10% off!

Until next time,
Tour Finder Puerto Rico`
  }
];

export default function MessageTemplateSelector({ 
  isOpen, 
  onClose, 
  channel, // 'email', 'sms', or 'whatsapp'
  customerData, // { customerName, customerEmail, customerPhone, tourTitle, tourDate, etc. }
  onSend // callback when message is sent
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Load templates on mount
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getCustomTemplates();
      let loadedTemplates = response.data || [];
      
      // Filter by channel type or show all
      if (channel === 'email') {
        loadedTemplates = loadedTemplates.filter(t => t.type === 'email' || t.type === 'all');
      } else if (channel === 'sms' || channel === 'whatsapp') {
        loadedTemplates = loadedTemplates.filter(t => t.type === 'sms' || t.type === 'whatsapp' || t.type === 'all');
      }
      
      // Merge with default templates (mark them as defaults)
      const defaultsForChannel = DEFAULT_TEMPLATES.filter(t => {
        if (channel === 'email') return true;
        if (channel === 'sms' || channel === 'whatsapp') return true;
        return true;
      }).map(t => ({ ...t, isDefault: true }));
      
      // Put custom templates first, then defaults
      setTemplates([...loadedTemplates, ...defaultsForChannel]);
    } catch (error) {
      console.error('Failed to load templates:', error);
      // Fall back to default templates
      setTemplates(DEFAULT_TEMPLATES.map(t => ({ ...t, isDefault: true })));
    }
    setLoading(false);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    // Replace variables with actual customer data
    setSubject(replaceTemplateVariables(template.subject || '', customerData));
    setMessage(replaceTemplateVariables(template.message || '', customerData));
    setEditMode(false);
  };

  const handleSend = () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    
    try {
      const customerEmail = customerData.customerEmail;
      const customerPhone = customerData.customerPhone;
      
      if (channel === 'email') {
        if (!customerEmail) {
          toast.error('No email address available');
          setSending(false);
          return;
        }
        const mailtoUrl = `mailto:${customerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
        window.location.href = mailtoUrl;
        toast.success('Opening email client...');
      } else if (channel === 'sms') {
        if (!customerPhone) {
          toast.error('No phone number available');
          setSending(false);
          return;
        }
        const cleanPhone = customerPhone.replace(/[\s()-]/g, '');
        const smsUrl = `sms:${cleanPhone}?body=${encodeURIComponent(message)}`;
        window.location.href = smsUrl;
        toast.success('Opening SMS app...');
      } else if (channel === 'whatsapp') {
        if (!customerPhone) {
          toast.error('No phone number available');
          setSending(false);
          return;
        }
        const cleanPhone = customerPhone.replace(/\D/g, '');
        const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
        window.open(waUrl, '_blank');
        toast.success('Opening WhatsApp...');
      }
      
      if (onSend) onSend({ channel, subject, message });
      onClose();
    } catch (error) {
      toast.error('Failed to open messaging app');
    }
    
    setSending(false);
  };

  const getChannelIcon = () => {
    switch (channel) {
      case 'email': return <Mail className="h-5 w-5 text-blue-600" />;
      case 'sms': return <MessageSquare className="h-5 w-5 text-green-600" />;
      case 'whatsapp': return <MessageCircle className="h-5 w-5 text-emerald-600" />;
      default: return <Send className="h-5 w-5" />;
    }
  };

  const getChannelName = () => {
    switch (channel) {
      case 'email': return 'Email';
      case 'sms': return 'SMS';
      case 'whatsapp': return 'WhatsApp';
      default: return 'Message';
    }
  };

  const getChannelColor = () => {
    switch (channel) {
      case 'email': return 'bg-blue-500 hover:bg-blue-600';
      case 'sms': return 'bg-green-500 hover:bg-green-600';
      case 'whatsapp': return 'bg-emerald-500 hover:bg-emerald-600';
      default: return 'bg-primary';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getChannelIcon()}
            Send {getChannelName()} to {customerData?.customerName || 'Customer'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Customer Info */}
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <div className="flex items-center gap-4 flex-wrap">
              {customerData?.customerName && (
                <span><strong>Name:</strong> {customerData.customerName}</span>
              )}
              {customerData?.customerEmail && channel === 'email' && (
                <span><strong>Email:</strong> {customerData.customerEmail}</span>
              )}
              {customerData?.customerPhone && channel !== 'email' && (
                <span><strong>Phone:</strong> {customerData.customerPhone}</span>
              )}
              {customerData?.tourTitle && (
                <span><strong>Tour:</strong> {customerData.tourTitle}</span>
              )}
            </div>
          </div>

          {/* Template Selection or Message Editor */}
          {!selectedTemplate ? (
            <>
              <div>
                <Label className="text-base font-semibold mb-2 block">Choose a Template</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Select a template below or create your own message
                </p>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="grid gap-2">
                  {templates.map((template) => (
                    <Card 
                      key={template.id} 
                      className="p-3 cursor-pointer hover:bg-gray-50 transition-colors border-l-4 border-l-transparent hover:border-l-primary"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{template.name}</span>
                          {template.isDefault && (
                            <Badge variant="secondary" className="text-xs">Default</Badge>
                          )}
                          <Badge variant="outline" className="text-xs capitalize">{template.type}</Badge>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                      {template.subject && channel === 'email' && (
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          Subject: {template.subject}
                        </p>
                      )}
                    </Card>
                  ))}
                  
                  {/* Custom Message Option */}
                  <Card 
                    className="p-3 cursor-pointer hover:bg-gray-50 transition-colors border-l-4 border-l-transparent hover:border-l-purple-500 border-dashed"
                    onClick={() => {
                      setSelectedTemplate({ id: 'custom', name: 'Custom Message', type: 'all' });
                      setSubject('');
                      setMessage('');
                      setEditMode(true);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-purple-500" />
                        <span className="font-medium text-purple-600">Write Custom Message</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </Card>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Selected Template Preview/Editor */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{selectedTemplate.name}</Badge>
                  {!editMode && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setEditMode(true)}
                      className="gap-1"
                    >
                      <Edit2 className="h-3 w-3" />
                      Edit
                    </Button>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setSelectedTemplate(null);
                    setSubject('');
                    setMessage('');
                    setEditMode(false);
                  }}
                >
                  Change Template
                </Button>
              </div>

              {/* Subject (email only) */}
              {channel === 'email' && (
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={!editMode}
                    className={!editMode ? 'bg-gray-50' : ''}
                    placeholder="Email subject..."
                  />
                </div>
              )}

              {/* Message */}
              <div>
                <Label>Message</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={!editMode}
                  className={`min-h-[200px] ${!editMode ? 'bg-gray-50' : ''}`}
                  placeholder="Type your message..."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {editMode ? 'Edit the message above before sending' : 'Click "Edit" to customize this message'}
                </p>
              </div>

              {/* Preview Badge */}
              {!editMode && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <Check className="h-4 w-4" />
                  <span>Message preview with customer data filled in</span>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {selectedTemplate && (
            <Button 
              onClick={handleSend} 
              disabled={sending || !message.trim()}
              className={`gap-2 ${getChannelColor()} text-white`}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                getChannelIcon()
              )}
              Send via {getChannelName()}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper hook for using the template selector
export function useMessageTemplateSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [channel, setChannel] = useState('email');
  const [customerData, setCustomerData] = useState({});

  const openSelector = (channelType, data) => {
    setChannel(channelType);
    setCustomerData(data);
    setIsOpen(true);
  };

  return {
    isOpen,
    setIsOpen,
    channel,
    customerData,
    openSelector,
    closeSelector: () => setIsOpen(false)
  };
}
